import Address from 'components/Address'
import Alert from 'components/Alert'
import Button from 'components/Button'
import Card from 'components/Card'
import {
  getNextRun,
  getStringFromCron,
} from 'components/CronConfigurator/utils'
import LoaderOverlay from 'components/LoaderOverlay'
import AdditionalServicesTable from 'components/OrderDetails/AdditionalServicesTable'
import ColiTable from 'components/OrderDetails/ColiTable'
import lodash, { get, isEmpty } from 'lodash'
import moment from 'moment'
import React, { useEffect } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from 'styled-components'

import { requestOrder } from '../actions'
import { makeSelectIsLoading, makeSelectOrder, selectError } from '../selectors'

const connectRedux = connect(
  createStructuredSelector({
    item: makeSelectOrder(),
    isLoading: makeSelectIsLoading(),
    error: selectError,
  }),
  { requestOrder },
)

const statusToI18n = (status) => {
  switch (status) {
    case 'pending':
      return i18n.t('status_cron_pending', 'Ausstehend')

    case 'failed':
      return i18n.t('status_cron_failed', 'Fehlgeschlagen')

    case 'complete':
      return i18n.t('status_cron_complete', 'Ausgeführt')

    default:
      return i18n.t('unknown', 'Unbekannt')
  }
}

const CronOrder = (props) => {
  const { item, isLoading, error, requestOrder } = props

  useEffect(() => {
    requestOrder(props.match.params.id)
  }, [])

  const { t } = useTranslation()
  const order = lodash.get(item, 'order', {})

  return (
    <Container className="mt-4">
      <LoaderOverlay fullsize isLoading={isLoading} />
      {error && (
        <Alert danger title={i18n.t('error', 'Fehler')}>
          {i18n.t(
            'error_fetching_order',
            ` Der Auftrag konnte nicht abgefragt werden. Bitte versuchen Sie es
          später erneut.`,
          )}
        </Alert>
      )}
      {item && (
        <>
          <Card noHeader>
            <Row>
              <Col className="d-flex justify-content-between">
                <Button light className="mt-1" to="/cronorders">
                  <i className="fas fa-arrow-left" /> &nbsp;
                  {i18n.t('back', 'Zurück')}
                </Button>
              </Col>
            </Row>
          </Card>
          <Card noHeader>
            <>
              <Row>
                <Col md="6">
                  <h3 className="mb-0">{`${i18n.t(
                    'cron_order',
                    'Dauerauftrag',
                  )} ${item.name}`}</h3>
                  <div className="text-muted">
                    vom {moment(item.created_at).format('DD.MM.YYYY HH:mm')}
                  </div>
                </Col>
                <Col md="3" className="text-right">
                  <h3>{`${i18n.t(
                    'crond_order_status',
                    'Status',
                  )}: ${statusToI18n(lodash.get(item, 'status'))}`}</h3>
                </Col>
                <Col md="3" className="text-right">
                  <h3>{`${i18n.t('product', 'Produkt')}: ${lodash.get(
                    order,
                    'sla',
                    i18n.t('unknown', 'Unbekannt'),
                  )}`}</h3>
                </Col>
              </Row>

              {!isEmpty(get(item, 'error')) && (
                <Row className="mt-2">
                  <Col md="12">
                    <Alert variant="danger">{get(item, 'error')}</Alert>
                  </Col>
                </Row>
              )}

              <Row className="mt-2">
                <Col md="6">
                  <h4>{i18n.t('execution_pattern', 'Ausführungsrhythmus')}</h4>
                  {getStringFromCron(item.cronPattern, item.cronConfig)}
                </Col>
                {lodash.get(item, 'cronConfig.cronType') !== 'once' && (
                  <Col md="6">
                    <h4>
                      {i18n.t('next_5_execution', 'Nächste 5 Ausführungen')}
                    </h4>
                    {lodash.map(
                      getNextRun(
                        lodash.get(item, 'cronPattern', ''),
                        moment().toISOString(),
                        5,
                        lodash.get(item, 'cronConfig.cronTimes', []),
                      ) || [],
                      (run) => (
                        <div>{run}</div>
                      ),
                    )}
                  </Col>
                )}
              </Row>

              <Row className="mt-4">
                <Col md="3">
                  <h4>{i18n.t('sender', 'Versender')}</h4>
                  <Address {...order.sender} />
                </Col>
                <Col md="3">
                  <h4>{i18n.t('billing_address', 'Rechnungsadresse')}</h4>
                  <Address {...order.invoice} />
                </Col>
                <Col md="3">
                  <h4>{i18n.t('recipient', 'Empfänger')}</h4>
                  <Address {...order.receiver} />
                </Col>
                {lodash.includes(order.additional_services || [], 'NES') && (
                  <Col md="3">
                    <h4>{i18n.t('neutral_sender', 'Neutralversender')}</h4>
                    <Address {...order.neutral} />
                  </Col>
                )}
              </Row>
              {(lodash.size(order.additional_services) > 0 ||
                order.cashondelivery) && (
                <Row className="mt-4">
                  <Col>
                    <h4>{i18n.t('additional_services', 'Zusatservices')}</h4>
                  </Col>
                  <Col md="12">
                    <AdditionalServicesTable order={order} />
                  </Col>{' '}
                </Row>
              )}

              <Row className="mt-2">
                <Col>
                  <h4>{i18n.t('colis_plural', 'Colis')}</h4>
                </Col>
                <Col md="12">
                  <ColiTable order={order} isCron />
                </Col>{' '}
              </Row>
            </>
          </Card>
        </>
      )}
    </Container>
  )
}

export default connectRedux(CronOrder)
